import { SUPPORTED_FILE_FORMAT , MESSAGES, ROLE_OPTIONS, ROLES} from "./constants";

/**
 * Validates file extension
 * @param {File} file - The uploaded file
 * @returns {boolean} - True if valid, throws error otherwise
 */
export function validateFileExtension(file) {
  const ext = ((file || {}).name || "").split(".").pop();
  const validExtensions = SUPPORTED_FILE_FORMAT;
  
  if (!validExtensions.includes(ext)) {
    throw new Error(MESSAGES.INVALID_FILE_FORMAT);
  }
  return true;
}



/**
 * Validates Excel headers against expected headers
 * Provides detailed error messages showing missing, extra, or mismatched columns
 * @param {Array} actualHeaders - Headers from uploaded file
 * @param {Array} expectedHeaders - Expected headers
 * @throws {Error} - Detailed header mismatch error with actionable information
 */
export function validateExcelHeaders(actualHeaders, expectedHeaders) {
  if (JSON.stringify(actualHeaders) !== JSON.stringify(expectedHeaders)) {
    // Find missing columns (in expected but not in actual)
    const missingColumns = expectedHeaders.filter((col) => 
      col && !actualHeaders.includes(col)
    );
    
    // Find extra columns (in actual but not in expected)
    const extraColumns = actualHeaders.filter((col) => 
      col && !expectedHeaders.includes(col)
    );
    
    // Check for column count mismatch
    const columnCountDiff = expectedHeaders.length - actualHeaders.length;
    
    // Build detailed error message parts
    const errorParts = [];
    
    if (missingColumns.length > 0) {
      errorParts.push(`Missing columns: ${missingColumns.map(c => `"${c}"`).join(', ')}`);
    }
    
    if (extraColumns.length > 0) {
      errorParts.push(`Unexpected columns: ${extraColumns.map(c => `"${c}"`).join(', ')}`);
    }
    
    if (columnCountDiff !== 0 && missingColumns.length === 0 && extraColumns.length === 0) {
      errorParts.push(`Expected ${expectedHeaders.length} columns but found ${actualHeaders.length}`);
    }
    
    // If no specific differences found, show column count info
    if (errorParts.length === 0) {
      errorParts.push(`Column structure mismatch. Expected ${expectedHeaders.length} columns, found ${actualHeaders.length}`);
    }
    
    const errorMessage = `Invalid file format. ${errorParts.join('. ')}. Please download a fresh template and try again.`;
    throw new Error(errorMessage);
  }
}

/**
 * Gets all available roles for a user within a specific reporting unit
 * Checks user presence in submitter, approver, regional_approver, and manager arrays
 * Returns roles sorted by priority (lowest priority first)
 * 
 * @param {Object} ruDetails - Reporting unit details object containing user assignments
 * @param {Array<string>} ruDetails.submitter - Array of user IDs assigned as submitters
 * @param {Array<string>} ruDetails.approver - Array of user IDs assigned as local approvers
 * @param {Array<string>} ruDetails.regional_approver - Array of user IDs assigned as regional approvers
 * @param {Array<string>} ruDetails.manager - Array of user IDs assigned as managers
 * @param {string} userId - User ID to check role assignments for
 * 
 * @returns {Array<Object>} - Array of role objects sorted by priority (ascending)
 * @returns {string} returns[].value - Role identifier (e.g., 'preparer', 'approver')
 * @returns {string} returns[].label - Display label for the role
 * @returns {string} returns[].color - Color code for UI representation
 * @returns {number} returns[].priority - Priority number (1=highest, 4=lowest)
 */
export const getAvailableRoles = (ruDetails,userId) => {
  try {
    const userRoles = [];
    // Create a map for O(1) lookup
    const roleMap = ROLE_OPTIONS.reduce((map, role) => {
      map[role.value] = role;
      return map;
    }, {});
    
    // Check each role type using the map
    if ((ruDetails?.submitter || []).includes(userId)) {
      userRoles.push({ ...roleMap[ROLES.LOCAL_PREPARER] });
    }
    
    if ((ruDetails?.approver || []).includes(userId)) {
      userRoles.push({ ...roleMap[ROLES.LOCAL_APPROVER] });
    }
    
    if ((ruDetails?.regional_approver || []).includes(userId)) {
      userRoles.push({ ...roleMap[ROLES.REGIONAL_APPROVER] });
    }
    
    if ((ruDetails?.manager || []).includes(userId)) {
      userRoles.push({ ...roleMap[ROLES.MANAGER] });
    }
    // Sort by priority ascending => lowest first
    userRoles?.sort((role1, role2) => role1?.priority - role2?.priority);
    return userRoles;
  } catch (err) {
    console.error("Error in getAvailableRoles:", err);
    return [];
  }
}

/**
 * Ensures the access hierarchy data (network, region, country) is available
 * in the Vuex store. If the data already exists, returns it directly.
 * If not, dispatches the fetchAccessHierarchy action and returns the result.
 * Guards against concurrent calls — if a fetch is already in-flight, subsequent
 * callers wait for the same promise instead of triggering duplicate API requests.
 *
 * @param {Object} store - The Vuex store instance (this.$store)
 * @param {Object} axios - The axios instance for API calls (this.axios)
 * @returns {Promise<Object>} The hierarchy data from the store
 */
export async function ensureAccessHierarchy(store, axios) {
  // Check both dynamic (m_varianceanalysis) and static (varianceanalysis) modules
  // The Actual/Forecast Cycle pages store hierarchy data in the dynamic module
  const dynamicState = store.state.m_varianceanalysis || {};
  const staticState = store.state.varianceanalysis || {};

  let hierarchyData = dynamicState.actualhierarchydata || staticState.actualhierarchydata;

  const hasValidData = hierarchyData
    && Object.keys(hierarchyData).length > 0
    && (
      (Array.isArray(hierarchyData.networklist) && hierarchyData.networklist.length > 0)
      || (Array.isArray(hierarchyData.regionlist) && hierarchyData.regionlist.length > 0)
      || (Array.isArray(hierarchyData.countrylist) && hierarchyData.countrylist.length > 0)
    );

  if (!hasValidData) {
    const modulePrefix = store.state.m_varianceanalysis ? 'm_varianceanalysis' : 'varianceanalysis';
    const filter = {
      filter: {
        cycletype: "Actual",
        type: "ru",
        role: "all",
        network: "all",
        region: "all",
        country: "all",
        reportingunit: "all",
      },
    };
    // Store action handles deduplication — concurrent dispatches reuse the same in-flight promise
    await store.dispatch(`${modulePrefix}/fetchAccessHierarchy`, {
      axios,
      filter,
    });
    hierarchyData = (store.state[modulePrefix] || {}).actualhierarchydata || {};
  }

  return hierarchyData;
}

/**
 * Builds name-to-ID maps for network, region, and country from hierarchy data.
 * Merges with any existing maps so that entries from paginated data are preserved.
 *
 * @param {Object} hierarchyData - The hierarchy response containing networklist, regionlist, countrylist
 * @param {Object} [existingMaps={}] - Optional existing maps to merge with
 * @param {Object} [existingMaps.networkNameIdMap={}]
 * @param {Object} [existingMaps.regionNameIdMap={}]
 * @param {Object} [existingMaps.countryNameIdMap={}]
 * @returns {{ networkNameIdMap: Object, regionNameIdMap: Object, countryNameIdMap: Object }}
 */
export function buildHierarchyMaps(hierarchyData, existingMaps = {}) {
  const networkNameIdMap = { ...(existingMaps.networkNameIdMap || {}) };
  const regionNameIdMap = { ...(existingMaps.regionNameIdMap || {}) };
  const countryNameIdMap = { ...(existingMaps.countryNameIdMap || {}) };

  if (!hierarchyData || typeof hierarchyData !== 'object') {
    return { networkNameIdMap, regionNameIdMap, countryNameIdMap };
  }

  (hierarchyData.networklist || []).forEach(item => {
    if (item && item._id && item.name) {
      networkNameIdMap[item.name] = item._id;
    }
  });
  (hierarchyData.regionlist || []).forEach(item => {
    if (item && item._id && item.name) {
      regionNameIdMap[item.name] = item._id;
    }
  });
  (hierarchyData.countrylist || []).forEach(item => {
    if (item && item._id && item.name) {
      countryNameIdMap[item.name] = item._id;
    }
  });

  return { networkNameIdMap, regionNameIdMap, countryNameIdMap };
}
